import useUserUiPreferenceStorageValue from "../hooks/useUserUiPreferenceStorageValue.js";
import React from "react";
import useMediaQuery, { MOBILE_QUERY } from "../hooks/useMediaQuery.js";
import MobileDispatch from "./MobileDispatch.jsx";
import useEveningOrderPool from "../hooks/useEveningOrderPool.js";
import useEveningGanttRail from "../hooks/useEveningGanttRail.js";
import useManualProductMixing, { COMPARTMENT_SHARING_POLICY, PRODUCT_COMPARTMENT_FILL_POLICY } from "../hooks/useManualProductMixing.js";
import DetailViewStageState from "./DetailViewStageState.jsx";
import DetailViewTabs from "./DetailViewTabs.jsx";
import ErrorsCenterPanel from "./ErrorsCenterPanel.jsx";
import FleetViewPanel from "./FleetViewPanel.jsx";
import FleetList from "./FleetList.jsx";
import ResultsPageChrome from "./ResultsPageChrome.jsx";
import DispatchContextMenu from "./resultsPageChrome/DispatchContextMenu.jsx";
import RouteAssignmentStepsPanel from "./RouteAssignmentStepsPanel.jsx";
import RouteFloatingMapPanel from "./RouteFloatingMapPanel.jsx";
import { useUserUiPreferences } from "../hooks/useUserUiPreferences.js";
import { buildErrorsCenterEntries } from "../utils/errorsCenterData.js";
import {
  persistStoredEveningRailVisibility,
  readStoredEveningRailVisibility,
  resolveEveningRailVisibilityForUser,
} from "../utils/eveningRailVisibility.js";
import { selectRouteHeaderData } from "../utils/resultsChromeData.js";
import {
  getResultsRightPanelAlertCountSnapshot,
  getResultsRightPanelWarningCountSnapshot,
  subscribeResultsRightPanel,
} from "../utils/rightPanelData.js";

function normalizeSourceChangesMenuPeriod(value) {
  const normalizedValue = String(value || "").trim().toLowerCase();
  return normalizedValue === "evening" || normalizedValue === "soir" ? "evening" : "day";
}

export default function ResultsWorkspacePanels({
  detailViewState,
  errorsCenterOpenRequest = null,
  highlightedRouteSegmentIndex = null,
  initialResultsData = null,
  interactions = {},
  mapRenderMeta = null,
  onClientSelect,
  onMapControllerReady,
  onMapRenderMetaChange,
  onRouteHighlightChange,
  onToggleView,
  selectionRequest,
  stateRef,
  workspaceStore,
  workspaceElements = null,
  workspaceUi,
}) {
  const routeMapAnchorElementRef = React.useRef(null);
  const mobile = useMediaQuery(MOBILE_QUERY);
  const assignment = workspaceUi?.assignment || null;
  const fleetCards = Array.isArray(workspaceUi?.fleetCards) ? workspaceUi.fleetCards.filter(Boolean) : [];
  const selectedClientId = workspaceUi?.selectedClientId || null;
  const trip = workspaceUi?.trip || null;
  const { userId } = useUserUiPreferences();
  const storedEveningVisibility = useUserUiPreferenceStorageValue("session", `grain-saas:results:evening-rail-visibility:v1:${encodeURIComponent(userId || "")}`);
  const selectedRouteFleetCard = fleetCards.find((card) => card?.assignmentId === assignment?.truck_id)
    || fleetCards.find((card) => card?.isSelectedAssignment)
    || null;
  const activeRouteStepsCard = selectedRouteFleetCard?.isOpen ? selectedRouteFleetCard : null;
  const workspaceState = workspaceStore.getState();
  const isFleetViewActive = detailViewState.activeView === "fleet";
  const [isRouteMapFloating, setIsRouteMapFloating] = React.useState(false);
  const [eveningRailVisibilityState, setEveningRailVisibilityState] = React.useState(() => ({
    isOpen: readStoredEveningRailVisibility(userId),
    userId,
  }));
  const isEveningRailOpen = resolveEveningRailVisibilityForUser(
    userId,
    eveningRailVisibilityState,
  );
  const [sourceChangesMenuOpenPeriod, setSourceChangesMenuOpenPeriod] = React.useState("");
  const [
    includeSuspendedDeliveriesInOptimization,
    setIncludeSuspendedDeliveriesInOptimization,
  ] = React.useState(false);
  const [isSameOdGroupingMutationPending, setIsSameOdGroupingMutationPending] = React.useState(false);
  const [isOptimizationInProgress, setIsOptimizationInProgress] = React.useState(false);
  const [isOptimizationPreviewInteractionLocked, setIsOptimizationPreviewInteractionLocked] = React.useState(false);
  const handleSameOdGroupingMutationStateChange = React.useCallback((isPending) => {
    setIsSameOdGroupingMutationPending(isPending === true);
  }, []);
  const handleOptimizationStateChange = React.useCallback((isActive) => {
    setIsOptimizationInProgress(isActive === true);
  }, []);
  const handleEveningRailOpenChange = React.useCallback((nextValue) => {
    const nextIsOpen = nextValue === true;
    setEveningRailVisibilityState({ isOpen: nextIsOpen, userId });
    persistStoredEveningRailVisibility(userId, nextIsOpen);
  }, [userId, storedEveningVisibility]);
  React.useEffect(() => {
    setEveningRailVisibilityState({
      isOpen: readStoredEveningRailVisibility(userId),
      userId,
    });
  }, [userId]);
  // Les onglets ne lisent que les deux compteurs : pas de nouveau rendu du Dispatch si les nombres ne changent pas.
  const snapshotAlertCountValue = React.useSyncExternalStore(
    subscribeResultsRightPanel,
    getResultsRightPanelAlertCountSnapshot,
    getResultsRightPanelAlertCountSnapshot,
  );
  const snapshotWarningCountValue = React.useSyncExternalStore(
    subscribeResultsRightPanel,
    getResultsRightPanelWarningCountSnapshot,
    getResultsRightPanelWarningCountSnapshot,
  );
  const resultPayload = workspaceState?.resultsData || initialResultsData || null;
  const manualProductMixing = useManualProductMixing(resultPayload);
  const productCompartmentFill = useManualProductMixing(resultPayload, PRODUCT_COMPARTMENT_FILL_POLICY);
  const compartmentSharing = useManualProductMixing(resultPayload, COMPARTMENT_SHARING_POLICY);
  const activeDispatchPeriod = String(
    resultPayload?.ui_dispatch_period || resultPayload?.dispatch_period || "day",
  ).trim().toLowerCase();
  const showEveningSourceChangesShortcut = isFleetViewActive
    && isEveningRailOpen
    && activeDispatchPeriod !== "evening"
    && activeDispatchPeriod !== "soir";
  const activeSourceChangesScopePeriod = normalizeSourceChangesMenuPeriod(
    interactions.chrome?.sourceChanges?.activeScopeDispatchPeriod || activeDispatchPeriod,
  );
  const handleSourceChangesMenuOpenChange = React.useCallback((nextOpen, requestedPeriod = "") => {
    const normalizedPeriod = normalizeSourceChangesMenuPeriod(
      requestedPeriod || activeSourceChangesScopePeriod,
    );
    setSourceChangesMenuOpenPeriod(nextOpen === true ? normalizedPeriod : "");
  }, [activeSourceChangesScopePeriod]);
  const handleOpenSourceChangesMenu = React.useCallback((requestedPeriod = "") => {
    setSourceChangesMenuOpenPeriod(normalizeSourceChangesMenuPeriod(
      requestedPeriod || activeSourceChangesScopePeriod,
    ));
  }, [activeSourceChangesScopePeriod]);
  React.useEffect(() => {
    if (!showEveningSourceChangesShortcut) {
      setSourceChangesMenuOpenPeriod((currentPeriod) => (
        currentPeriod === "evening" && activeSourceChangesScopePeriod !== "evening" ? "" : currentPeriod
      ));
    }
  }, [activeSourceChangesScopePeriod, showEveningSourceChangesShortcut]);
  const dispatchDate = String(
    resultPayload?.dispatch_date || resultPayload?.planning_summary?.dispatch_date || "",
  ).trim();
  const eveningGanttRailState = useEveningGanttRail({
    dispatchDate,
    enabled: showEveningSourceChangesShortcut,
  });
  const eveningOrderPool = useEveningOrderPool(
    showEveningSourceChangesShortcut ? eveningGanttRailState.payload : resultPayload,
    { unavailableReason: showEveningSourceChangesShortcut
      ? eveningGanttRailState.errorMessage || (eveningGanttRailState.isLoading ? "Chargement du dispatch Soir en cours." : "")
      : "" },
  );
  React.useEffect(() => {
    setSourceChangesMenuOpenPeriod("");
  }, [dispatchDate, detailViewState.activeView]);
  const runtimeRevision = String(resultPayload?.runtime_revision || "").trim();
  const eveningSourceChanges = interactions.chrome?.sourceChangesByPeriod?.evening
    || interactions.sourceChangesByPeriod?.evening
    || null;
  React.useEffect(() => {
    if (
      !showEveningSourceChangesShortcut
      || !dispatchDate
      || typeof eveningSourceChanges?.onEnsure !== "function"
    ) {
      return;
    }
    void eveningSourceChanges.onEnsure();
  }, [
    dispatchDate,
    eveningSourceChanges?.onEnsure,
    runtimeRevision,
    showEveningSourceChangesShortcut,
  ]);
  const sharedChromeInteractions = {
    ...(interactions.chrome || {}),
    optimizationScope: {
      dispatchDate,
      dispatchPeriod: normalizeSourceChangesMenuPeriod(activeDispatchPeriod),
      showPeriodMenu: showEveningSourceChangesShortcut,
      eveningGanttRailState,
    },
    manualProductMixing,
    productCompartmentFill,
    compartmentSharing,
    eveningOrderPool,
    activeDetailView: detailViewState.activeView,
    isSameOdGroupingMutationPending,
    isOptimizationInProgress,
    isOptimizationPreviewInteractionLocked,
    onSameOdGroupingMutationStateChange: handleSameOdGroupingMutationStateChange,
    onOptimizationStateChange: handleOptimizationStateChange,
    isSourceChangesMenuOpen: Boolean(sourceChangesMenuOpenPeriod),
    sourceChangesMenuOpenPeriod,
    onSourceChangesMenuOpenChange: handleSourceChangesMenuOpenChange,
    onOpenSourceChangesMenu: handleOpenSourceChangesMenu,
    showEveningSourceChangesShortcut,
    onIncludeSuspendedDeliveriesInOptimizationChange: setIncludeSuspendedDeliveriesInOptimization,
    routeMapFloating: {
      isOpen: isRouteMapFloating,
      onToggle(nextValue, anchorElement = null) {
        if (anchorElement instanceof HTMLElement) {
          routeMapAnchorElementRef.current = anchorElement;
        }

        if (!nextValue) {
          setIsRouteMapFloating(false);
          return;
        }

        setIsRouteMapFloating(true);
      },
    },
  };
  const snapshotWarningCount = snapshotWarningCountValue == null ? Number.NaN : Number(snapshotWarningCountValue);
  const warningCount = Number.isFinite(snapshotWarningCount)
    ? snapshotWarningCount
    : buildErrorsCenterEntries(resultPayload, { viewName: "warnings" }).length;
  const snapshotAlertCount = snapshotAlertCountValue == null ? Number.NaN : Number(snapshotAlertCountValue);
  const alertCount = Number.isFinite(snapshotAlertCount)
    ? snapshotAlertCount
    : buildErrorsCenterEntries(resultPayload, { viewName: "errors" }).length;
  const selectedHeaderModel = React.useMemo(() => {
    if (!assignment) {
      return null;
    }

    return selectRouteHeaderData(assignment, trip);
  }, [assignment, trip]);

  if (mobile && (!detailViewState.activeView || isFleetViewActive)) {
    return <MobileDispatch cards={fleetCards} result={resultPayload} state={workspaceState} stateRef={stateRef} />;
  }

  return (
    <DispatchContextMenu>
      {!isFleetViewActive ? (
        <FleetList
          cards={workspaceUi?.fleetCards || []}
          mountNode={workspaceElements?.fleetColumnRoot || null}
          routeCanvasStepsPanel
          selectedHeaderModel={selectedHeaderModel}
        />
      ) : null}

      {!isFleetViewActive ? (
        <RouteAssignmentStepsPanel
          card={activeRouteStepsCard}
          mountNode={workspaceElements?.routeStepsRoot || null}
          state={workspaceState}
        />
      ) : null}

      {!isFleetViewActive ? (
        <RouteFloatingMapPanel
          anchorElement={routeMapAnchorElementRef.current}
          assignment={assignment}
          isRouteViewActive={!detailViewState.activeView}
          isMapFloating={isRouteMapFloating}
          mountNode={workspaceElements?.mapRoot || null}
          onClientSelect={onClientSelect}
          onControllerReady={onMapControllerReady}
          onMapFloatingChange={setIsRouteMapFloating}
          onMapRenderMetaChange={onMapRenderMetaChange}
          onRouteHighlightChange={onRouteHighlightChange}
          selectedClientId={selectedClientId}
          selectionRequest={selectionRequest}
          showInlineHeader={false}
          stateRef={stateRef}
          trip={trip}
        />
      ) : null}

      <ResultsPageChrome
        assignment={assignment}
        highlightedRouteSegmentIndex={highlightedRouteSegmentIndex}
        includeSuspendedDeliveriesInOptimization={includeSuspendedDeliveriesInOptimization}
        interactions={sharedChromeInteractions}
        mapRenderMeta={mapRenderMeta}
        mountNodes={workspaceElements}
        state={workspaceState}
        trip={trip}

      />

      <DetailViewTabs
        activeView={detailViewState.activeView}
        alertCount={alertCount}
        mountNode={workspaceElements?.detailViewTabsRoot || null}
        onToggleView={onToggleView}
        warningCount={warningCount}
      />

      <DetailViewStageState
        activeView={detailViewState.activeView}
        mountNode={workspaceElements?.analysisStageRoot || null}
        standardViewNode={workspaceElements?.standardViewRoot || null}
      />

      <FleetViewPanel
        eveningGanttRailState={eveningGanttRailState}
        assignment={assignment}
        includeSuspendedDeliveriesInOptimization={includeSuspendedDeliveriesInOptimization}
        interactions={{
          ...interactions,
          chrome: sharedChromeInteractions,
        }}
        isActiveView={isFleetViewActive}
        isEveningRailOpen={isEveningRailOpen}
        mountNode={workspaceElements?.fleetViewRoot || null}
        onClientSelect={onClientSelect}
        onMapControllerReady={onMapControllerReady}
        onMapRenderMetaChange={onMapRenderMetaChange}
        onRouteHighlightChange={onRouteHighlightChange}
        isOptimizationInProgress={isOptimizationInProgress}
        onOptimizationPreviewInteractionLockChange={setIsOptimizationPreviewInteractionLocked}
        selectionRequest={selectionRequest}
        state={workspaceState}
        stateRef={stateRef}
        selectedClientId={selectedClientId}
        trip={trip}
        onIncludeSuspendedDeliveriesInOptimizationChange={setIncludeSuspendedDeliveriesInOptimization}
        onEveningRailOpenChange={handleEveningRailOpenChange}
      />

      <ErrorsCenterPanel
        activeView={detailViewState.activeView}
        interactions={interactions}
        mountNode={workspaceElements?.warningsCenterRoot || null}
        onActivateView={detailViewState.activateView}
        openRequest={errorsCenterOpenRequest}
        resultPayload={workspaceState?.resultsData || initialResultsData}
        viewName="warnings"
      />

      <ErrorsCenterPanel
        activeView={detailViewState.activeView}
        interactions={interactions}
        mountNode={workspaceElements?.errorsCenterRoot || null}
        onActivateView={detailViewState.activateView}
        openRequest={errorsCenterOpenRequest}
        resultPayload={workspaceState?.resultsData || initialResultsData}
        viewName="errors"
      />
    </DispatchContextMenu>
  );
}
